import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefCallback,
} from "react";

/** Movement past this (px) counts as drag-pan, not a card click. */
const DRAG_THRESHOLD_PX = 8;

const INTERACTIVE_SELECTOR =
  "button, a, input, textarea, select, [role='button'], [contenteditable='true']";

function isScrollableHorizontally(el: HTMLElement): boolean {
  return el.scrollWidth > el.clientWidth + 1;
}

function shouldIgnoreDragStart(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return true;
  return Boolean(target.closest(INTERACTIVE_SELECTOR));
}

function attachHorizontalScrollRow(
  container: HTMLElement,
  setDragging: (dragging: boolean) => void,
): () => void {
  const dragState = {
    active: false,
    pointerId: -1,
    startX: 0,
    scrollLeft: 0,
    moved: false,
    suppressClick: false,
  };

  const onWheel = (e: WheelEvent) => {
    if (e.deltaY === 0) return;
    if (!isScrollableHorizontally(container)) return;
    e.preventDefault();
    e.stopPropagation();
    container.scrollLeft += e.deltaY;
  };

  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    if (shouldIgnoreDragStart(e.target)) return;
    if (!isScrollableHorizontally(container)) return;

    dragState.active = true;
    dragState.pointerId = e.pointerId;
    dragState.startX = e.clientX;
    dragState.scrollLeft = container.scrollLeft;
    dragState.moved = false;
    dragState.suppressClick = false;
    setDragging(true);
    // Do not setPointerCapture here — it steals the click target from plan cards.
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!dragState.active || e.pointerId !== dragState.pointerId) return;

    const dx = e.clientX - dragState.startX;
    if (!dragState.moved && Math.abs(dx) > DRAG_THRESHOLD_PX) {
      dragState.moved = true;
      try {
        container.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }
    if (!dragState.moved) return;

    e.preventDefault();
    container.scrollLeft = dragState.scrollLeft - dx;
  };

  const endPointerDrag = (e: PointerEvent) => {
    if (!dragState.active || e.pointerId !== dragState.pointerId) return;

    if (dragState.moved) {
      dragState.suppressClick = true;
    }

    dragState.active = false;
    setDragging(false);

    try {
      if (container.hasPointerCapture(e.pointerId)) {
        container.releasePointerCapture(e.pointerId);
      }
    } catch {
      // ignore
    }
  };

  const onClickCapture = (e: MouseEvent) => {
    if (!dragState.suppressClick) return;
    e.preventDefault();
    e.stopPropagation();
    dragState.suppressClick = false;
    dragState.moved = false;
  };

  container.addEventListener("wheel", onWheel, { passive: false });
  container.addEventListener("pointerdown", onPointerDown);
  container.addEventListener("pointermove", onPointerMove);
  container.addEventListener("pointerup", endPointerDrag);
  container.addEventListener("pointercancel", endPointerDrag);
  container.addEventListener("click", onClickCapture, true);

  return () => {
    container.removeEventListener("wheel", onWheel);
    container.removeEventListener("pointerdown", onPointerDown);
    container.removeEventListener("pointermove", onPointerMove);
    container.removeEventListener("pointerup", endPointerDrag);
    container.removeEventListener("pointercancel", endPointerDrag);
    container.removeEventListener("click", onClickCapture, true);
    setDragging(false);
  };
}

export type UseHorizontalScrollRowResult<T extends HTMLElement> = Readonly<{
  ref: RefCallback<T>;
  isDragging: boolean;
}>;

export type UseHorizontalScrollRowOptions = Readonly<{
  /** When false, listeners are detached (e.g. dialog closed or row still loading). */
  enabled?: boolean;
}>;

/**
 * Horizontal scroll row: wheel → scrollLeft, pointer-drag on cards to pan.
 * Listeners attach when the row DOM node mounts (e.g. after plans finish loading).
 */
export function useHorizontalScrollRow<T extends HTMLElement>(
  options: UseHorizontalScrollRowOptions = {},
): UseHorizontalScrollRowResult<T> {
  const { enabled = true } = options;
  const [container, setContainer] = useState<T | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const setDraggingRef = useRef(setIsDragging);
  setDraggingRef.current = setIsDragging;

  const ref = useCallback((node: T | null) => {
    setContainer(node);
  }, []);

  useEffect(() => {
    if (!container || !enabled) {
      return undefined;
    }
    return attachHorizontalScrollRow(container, (dragging) =>
      setDraggingRef.current(dragging),
    );
  }, [container, enabled]);

  return { ref, isDragging };
}
